import { useMemo } from 'react';
import { useNavigate, useMutation, useI18n } from '@ywwwtseng/react-kit';
import { type FormElement, Form, validator } from '../../lib/react-form';
import { useAccountVerificationForm } from '../../hooks/useAccountVerificationForm';
import { EmailOtpButton } from '../../components/common/EmailOtpButton';

export default function AccountVerificationScreen({
  params,
}: {
  params: { back?: string };
}) {
  const { t } = useI18n();
  const { mutate, isLoading } = useMutation('me:verify');
  const navigate = useNavigate();
  const form = useAccountVerificationForm();

  const elements = useMemo<{ row: FormElement[] }[]>(() => {
    return [
      {
        row: [
          {
            ui: 'description',
            text: t('account_verification.description'),
          },
        ],
      },
      {
        row: [
          {
            ui: 'text',
            label: t('account_verification.email'),
            name: 'email',
            validate: [
              validator.required({
                message: t('validation.required', {
                  name: t('account_verification.email'),
                }),
              }),
              validator.email({
                message: t('validation.invalid', {
                  name: t('account_verification.email'),
                }),
              }),
            ],
            props: {
              placeholder: t('account_verification.email'),
              disabled: !!form.values.verify_confirm,
            },
          },
          {
            ui: 'custom',
            type: 'boolean',
            width: '140px',
            render: ({ values, setValue }) => {
              return (
                <EmailOtpButton
                  disabled={!!values.verify_confirm}
                  email={values.email as string}
                  onSuccess={() => {
                    setValue('send_otp_confirm', true);
                  }}
                />
              );
            },
            name: 'send_otp_confirm',
          },
        ],
      },
      {
        row: [
          {
            ui: 'text',
            label: 'OTP',
            name: 'otp',
            props: {
              placeholder: 'OTP',
              disabled: !!form.values.verify_confirm,
            },
          },
        ],
      },
    ];
  }, [form.values]);

  return (
    <div className="flex flex-col justify-start w-full h-full gap-2 py-3 px-5">
      <Form
        isLoading={isLoading}
        name="account_verification"
        {...form}
        elements={elements}
        submit={{
          label: t('common.verify'),
        }}
        onSubmit={(values) => {
          void mutate({
            first_name: values.first_name,
            last_name: values.last_name,
            birth_date: values.birth_date,
            gender: values.gender,
            email: values.email,
            otp: values.otp,
          }).then((result) => {
            if (result.ok) {
              form.reset();
              if (params.back) {
                navigate(params.back, { type: 'replace' });
              } else {
                navigate(-1);
              }
            }
          });
        }}
      />
    </div>
  );
}
